// ══════════════════════════════════════════════════════════════════════
// Trade Manager Section (Sprint 3 Phase 4)
//
// Renders the active-trade state inside a RunnerStatusWidget card.
// Only displayed when trade_manager_state.in_trade === true.
//
// Shows: direction, entry, SL (with BE-trail / runner indicator),
// remaining qty, partial-exit checklist, bars in trade,
// and a Force-Exit button (POST /api/runners/{key}/force-exit).
// ══════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { theme, fonts } from "../theme";
import { API_BASE } from "../config";
import LiveTradeChart from "./LiveTradeChart";

function fmtPrice(p) {
  if (p === null || p === undefined) return "—";
  return Number(p).toFixed(2);
}

function priceRR(entry, sl_dist, current_price, direction) {
  if (!sl_dist || sl_dist <= 0) return 0;
  const move = direction === "long" ? current_price - entry : entry - current_price;
  return move / sl_dist;
}

function slTypeLabel(state) {
  if (state.runner_active) return "Runner-Trail";
  if (state.be_trail_active) return "BE-Trail";
  if (state.be_triggered) return "BE-Locked";
  if (state.trailing_active) return "Trailing";
  return "Initial";
}

function slTypeColor(state) {
  if (state.runner_active) return theme.purpleText;
  if (state.be_trail_active) return theme.blueText;
  if (state.be_triggered) return theme.greenText;
  if (state.trailing_active) return theme.cyanText;
  return theme.text3;
}

export default function TradeManagerSection({ strategyKey, state }) {
  const [forcing, setForcing] = useState(false);
  const [forceError, setForceError] = useState(null);

  if (!state || !state.in_trade) return null;

  const direction = state.direction || "long";
  const isLong = direction === "long";
  const remaining = state.remaining_qty ?? 0;
  const total = state.original_qty ?? 0;
  const filled = total - remaining;

  const handleForceExit = async () => {
    if (!confirm(
      `Force-Exit Trade: ${strategyKey}\n\n` +
      `Aktuelle Position: ${direction.toUpperCase()} ${remaining} contracts\n` +
      `Diese Aktion schließt den Trade SOFORT mit Market-Order.\n\n` +
      `Fortfahren?`
    )) return;

    setForcing(true);
    setForceError(null);
    try {
      const url = `${API_BASE}/runners/${encodeURIComponent(strategyKey)}/force-exit`;
      const res = await fetch(url, { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        setForceError(data.detail || `HTTP ${res.status}`);
      }
    } catch (e) {
      setForceError(String(e));
    } finally {
      setForcing(false);
    }
  };

  // Partial-exit checklist data
  const tpDone = Array.isArray(state.tp_done) ? state.tp_done : [false, false, false, false];
  const tpLevels = Array.isArray(state.tp_levels) ? state.tp_levels : [0, 0, 0, 0];
  const partialPlan = Array.isArray(state.partial_plan) ? state.partial_plan : [0, 0, 0, 0];

  // Direction colors
  const dirBg = isLong ? theme.greenLight : theme.redLight;
  const dirText = isLong ? theme.greenText : theme.redText;
  const dirDot = isLong ? theme.green : theme.red;

  return (
    <div style={{
      marginTop: 14,
      padding: 14,
      background: dirBg,
      borderRadius: 10,
      border: `1px solid ${dirDot}40`,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>{isLong ? "📈" : "📉"}</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: dirText, letterSpacing: "0.6px", fontFamily: fonts.mono }}>
            ACTIVE TRADE — {direction.toUpperCase()} {remaining} CONTRACTS
          </span>
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
          background: "white", color: dirText, fontFamily: fonts.mono,
          border: `1px solid ${dirDot}40`,
        }}>
          {filled}/{total} EXITED
        </span>
      </div>

      {/* Core stats: Entry / SL / Bars */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14,
        padding: "10px 12px", background: "white", borderRadius: 8,
      }}>
        {/* Entry */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 3 }}>
            Entry
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: fonts.mono, color: theme.text }}>
            {fmtPrice(state.entry_price)}
          </div>
        </div>

        {/* SL */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 3 }}>
            Current SL
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: fonts.mono, color: theme.text, display: "flex", alignItems: "baseline", gap: 6 }}>
            {fmtPrice(state.sl_price)}
            {state.sl_price !== state.initial_sl && (
              <span style={{ fontSize: 9, color: theme.text4 }}>
                (init: {fmtPrice(state.initial_sl)})
              </span>
            )}
          </div>
          <div style={{ fontSize: 9, fontWeight: 600, color: slTypeColor(state), marginTop: 2 }}>
            {slTypeLabel(state)}
          </div>
        </div>

        {/* Bars in trade */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 3 }}>
            Bars in Trade
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: fonts.mono, color: theme.text }}>
            {state.bars_in_trade ?? 0}
            <span style={{ fontSize: 9, color: theme.text4, marginLeft: 4 }}>
              / {state.max_bars_in_trade ?? "∞"}
            </span>
          </div>
        </div>
      </div>

      {/* Partial-exit checklist */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>
          Partial Exits
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[0, 1, 2, 3].map((i) => {
            const done = tpDone[i];
            const level = tpLevels[i];
            const qty = partialPlan[i];
            // Skip TP4 row entirely if pct is 0 (cleaner)
            if (qty === 0 && i === 3) return null;
            // For TP3/TP4 when runner_active is true, mark them as skipped
            const skippedForRunner = state.runner_active && i >= 2 && !done;
            return (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "6px 10px", background: "white", borderRadius: 6,
                opacity: skippedForRunner ? 0.5 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: done ? theme.green : theme.tint,
                    color: done ? "white" : theme.text4,
                    fontSize: 10, fontWeight: 800,
                  }}>
                    {done ? "✓" : i + 1}
                  </span>
                  <span style={{ fontFamily: fonts.mono, fontSize: 11, fontWeight: 600, color: theme.text }}>
                    TP{i + 1} @ {fmtPrice(level)}
                  </span>
                  <span style={{ fontSize: 10, color: theme.text3 }}>
                    ({qty} {qty === 1 ? "contract" : "contracts"})
                  </span>
                </div>
                <span style={{ fontSize: 9, color: skippedForRunner ? theme.text4 : (done ? theme.greenText : theme.text3), fontWeight: 600 }}>
                  {done ? "filled" : skippedForRunner ? "skipped (runner)" : "pending"}
                </span>
              </div>
            );
          })}

          {/* Runner row — when runner_active */}
          {state.runner_active && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "6px 10px", background: theme.purpleLight, borderRadius: 6,
              border: `1px solid ${theme.purple}30`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, lineHeight: 1 }}>🏃</span>
                <span style={{ fontFamily: fonts.mono, fontSize: 11, fontWeight: 700, color: theme.purpleText }}>
                  Runner active
                </span>
                <span style={{ fontSize: 10, color: theme.text3 }}>
                  ({remaining} {remaining === 1 ? "contract" : "contracts"})
                </span>
              </div>
              <span style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.purpleText, fontWeight: 700 }}>
                SL: {fmtPrice(state.runner_sl)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sprint 3 Phase 5: Live trade chart */}
      <LiveTradeChart strategyKey={strategyKey} tradeState={state} />

      {/* Force-Exit Button */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <button
          onClick={handleForceExit}
          disabled={forcing}
          style={{
            fontSize: 11, fontWeight: 700,
            padding: "8px 14px", borderRadius: 8,
            border: `1px solid ${theme.red}40`,
            background: forcing ? theme.tint : theme.redLight,
            color: forcing ? theme.text3 : theme.redText,
            cursor: forcing ? "wait" : "pointer",
            fontFamily: fonts.body,
            transition: "all 0.15s",
          }}
        >
          {forcing ? "Closing..." : "🔴 Force Exit Trade"}
        </button>
        {forceError && (
          <div style={{
            fontSize: 10, color: theme.redText,
            padding: "6px 10px", background: "white",
            borderRadius: 6, border: `1px solid ${theme.red}30`,
          }}>
            Error: {forceError}
          </div>
        )}
      </div>
    </div>
  );
}
